import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { AlertTriangle, Info, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface RecipeQualitySectionProps {
    qualityScore?: number | null;
    qualityExplanation?: string | null;
    className?: string;
}

export const RecipeQualitySection = ({
    qualityScore,
    qualityExplanation,
    className
}: RecipeQualitySectionProps) => {
    const isMobile = useIsMobile();

    // Only show if we have a score and explanation
    if (!qualityScore || !qualityExplanation) {
        return null;
    }

    // Determine quality level and styling
    const getQualityInfo = (score: number) => {
        if (score >= 90) {
            return {
                level: "מעולה",
                color: "text-amber-600",
                bgColor: "bg-amber-50",
                borderColor: "border-amber-200",
                iconColor: "text-amber-500",
                badgeVariant: "outline" as const
            };
        } else if (score >= 80) {
            return {
                level: "טוב מאוד",
                color: "text-amber-600",
                bgColor: "bg-amber-50",
                borderColor: "border-amber-200",
                iconColor: "text-amber-500",
                badgeVariant: "outline" as const
            };
        } else if (score >= 70) {
            return {
                level: "טוב",
                color: "text-amber-600",
                bgColor: "bg-amber-50",
                borderColor: "border-amber-200",
                iconColor: "text-amber-500",
                badgeVariant: "outline" as const
            };
        } else if (score >= 50) {
            return {
                level: "בינוני",
                color: "text-amber-600",
                bgColor: "bg-amber-50",
                borderColor: "border-amber-200",
                iconColor: "text-amber-500",
                badgeVariant: "outline" as const
            };
        } else {
            return {
                level: "נמוך",
                color: "text-amber-600",
                bgColor: "bg-amber-50",
                borderColor: "border-amber-200",
                iconColor: "text-amber-500",
                badgeVariant: "outline" as const
            };
        }
    };

    const qualityInfo = getQualityInfo(qualityScore);

    return (
        <Card
            className={cn(
                "border-l-4 border-l-orange-300 bg-gradient-to-r from-orange-50/80 to-orange-100/60 shadow-sm",
                className
            )}
            dir="rtl"
        >
            <div className={cn("p-4 space-y-3", isMobile && "p-3 space-y-2")}>
                <div className="flex items-center gap-3">

                    <div className="flex-1">
                        <div className="flex items-center justify-between" dir="rtl">
                            {/* Title and Badge - Right side with closer spacing */}
                            <div className="flex items-center gap-1">
                                <h4 className={cn(
                                    "font-semibold text-right",
                                    qualityInfo.color,
                                    isMobile ? "text-base" : "text-lg"
                                )}>
                                    איכות המתכון
                                </h4>
                                <Badge
                                    variant={qualityInfo.badgeVariant}
                                    className={cn(
                                        "text-xs font-medium",
                                        isMobile ? "px-2 py-0.5" : "px-2.5 py-1"
                                    )}
                                >
                                    {qualityInfo.level}
                                </Badge>
                            </div>

                            {/* Stars - Most left (third child in RTL) */}
                            <div className="flex items-center gap-1" dir="ltr">
                                {[1, 2, 3, 4, 5].map((star) => {
                                    // Convert 0-100 score to 0-5 stars with half-star precision
                                    // For scores 90+, always show 5 stars
                                    const starValue = qualityScore >= 90 ? 5 : (qualityScore / 100) * 5;
                                    const isFilled = star <= Math.floor(starValue);
                                    const isHalfFilled = star === Math.ceil(starValue) && starValue % 1 >= 0.5;

                                    if (isFilled) {
                                        // Fully filled star
                                        return (
                                            <Star
                                                key={star}
                                                className={cn(
                                                    isMobile ? "h-3 w-3" : "h-3.5 w-3.5",
                                                    qualityInfo.iconColor
                                                )}
                                                fill="currentColor"
                                            />
                                        );
                                    } else if (isHalfFilled) {
                                        // Half-filled star using CSS clip-path
                                        return (
                                            <div key={star} className="relative">
                                                <Star
                                                    className={cn(
                                                        isMobile ? "h-3 w-3" : "h-3.5 w-3.5",
                                                        "text-gray-300"
                                                    )}
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1"
                                                />
                                                <Star
                                                    className={cn(
                                                        isMobile ? "h-3 w-3" : "h-3.5 w-3.5",
                                                        qualityInfo.iconColor,
                                                        "absolute top-0 left-0"
                                                    )}
                                                    fill="currentColor"
                                                    style={{ clipPath: 'polygon(0 0, 50% 0, 50% 100%, 0 100%)' }}
                                                />
                                            </div>
                                        );
                                    } else {
                                        // Empty star
                                        return (
                                            <Star
                                                key={star}
                                                className={cn(
                                                    isMobile ? "h-3 w-3" : "h-3.5 w-3.5",
                                                    "text-gray-300"
                                                )}
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="1"
                                            />
                                        );
                                    }
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={cn(
                    "text-right leading-relaxed",
                    qualityInfo.color,
                    isMobile ? "text-sm" : "text-base"
                )}>
                    <p className="font-medium">
                        {qualityExplanation}
                    </p>
                </div>
            </div>
        </Card>
    );
};
